import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { fetchTesterUserProfile } from './strategyTesterDataFeeds';
import {
  fetchStrategyArchitectSchema,
  fetchStrategyDslDraft,
  compileStrategyDsl,
  createBuilderStrategyFromDsl,
  fetchVixAiConversation,
  generateStrategyDslDraft,
  listStrategyDslDrafts,
  listVixAiConversations,
  saveStrategyDslDraft,
  sendVixAiMessage,
  validateStrategyDsl,
} from './strategyArchitectDataFeeds';
import './StrategyArchitectPage.css';

const EXAMPLE_PROMPT = `Create a non-indicator Asian session breakout strategy that marks the session range, keeps the range box on the chart, buys when London breaks above the range and retests it, sells when London breaks below and retests it, allows max 2 trades, uses breakeven after 300 points, trailing stop after 600 points, shows an info panel with session state, range high/low, active signal, risk percent, and open trades.`;
const INITIAL_VIXAI_MESSAGES = [
  {
    role: 'assistant',
    content: 'Describe the EA you want. I will turn it into a structured Builder DSL draft, validate it, and tell you whether it can become a Builder prototype.',
    actions: ['generate_dsl', 'analyze_readiness'],
  },
];

function downloadJson(filename, payload) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function safeFileName(value, fallback = 'strategy-dsl') {
  return String(value || fallback)
    .replace(/[^\w.-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '') || fallback;
}

function validationTone(validation) {
  if (!validation) return 'neutral';
  if (!validation.valid) return 'failed';
  if (validation.certification === 'certification_candidate') return 'candidate';
  return 'experimental';
}

function providerModeLabel(provider) {
  if (provider?.mode === 'openai_structured_dsl') return 'OpenAI-backed VixAI';
  if (provider?.mode === 'anthropic_structured_dsl') return 'Claude-backed VixAI';
  if (provider?.mode === 'local_structured_scaffold') return 'Structured scaffold';
  if (provider?.mode === 'local_structured_vixai') return 'Local VixAI fallback';
  return 'DSL validator';
}

function formatShortDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function StrategyArchitectPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('userToken') || '';
  const [profile, setProfile] = useState(null);
  const [schema, setSchema] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [prompt, setPrompt] = useState(EXAMPLE_PROMPT);
  const [dsl, setDsl] = useState(null);
  const [dslText, setDslText] = useState('');
  const [validation, setValidation] = useState(null);
  const [compileResult, setCompileResult] = useState(null);
  const [provider, setProvider] = useState(null);
  const [vixAiConversationId, setVixAiConversationId] = useState(null);
  const [vixAiConversations, setVixAiConversations] = useState([]);
  const [vixAiMessages, setVixAiMessages] = useState(INITIAL_VIXAI_MESSAGES);
  const [vixAiInput, setVixAiInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const isSignedIn = Boolean(token);
  const hasVixPremium = profile?.testerPlan === 'premium' || !!profile?.canExportMq5;
  const validationClass = validationTone(validation);
  const dslStats = useMemo(() => ({
    inputs: Array.isArray(dsl?.customInputs) ? dsl.customInputs.length : 0,
    ruleGroups: Array.isArray(dsl?.rules) ? dsl.rules.length : 0,
    visuals: Array.isArray(dsl?.visuals) ? dsl.visuals.length : 0,
    panels: Array.isArray(dsl?.panels) ? dsl.panels.length : 0,
  }), [dsl]);

  const refreshDrafts = useCallback(async () => {
    if (!token || !hasVixPremium) return;
    const response = await listStrategyDslDrafts(token);
    setDrafts(Array.isArray(response.drafts) ? response.drafts : []);
  }, [hasVixPremium, token]);

  const refreshVixAiConversations = useCallback(async () => {
    if (!token || !hasVixPremium) return;
    const response = await listVixAiConversations(token);
    setVixAiConversations(Array.isArray(response.conversations) ? response.conversations : []);
  }, [hasVixPremium, token]);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      setLoading(true);
      setError('');
      try {
        const schemaResponse = await fetchStrategyArchitectSchema();
        if (cancelled) return;
        setSchema(schemaResponse.schema || null);
        setProvider(schemaResponse.aiProvider || null);

        if (token) {
          const profileResponse = await fetchTesterUserProfile(token);
          if (cancelled) return;
          const nextProfile = profileResponse.user || null;
          setProfile(nextProfile);
          const premium = nextProfile?.testerPlan === 'premium' || !!nextProfile?.canExportMq5;
          if (premium) {
            const [draftResponse, conversationResponse] = await Promise.all([
              listStrategyDslDrafts(token),
              listVixAiConversations(token),
            ]);
            if (!cancelled) {
              setDrafts(Array.isArray(draftResponse.drafts) ? draftResponse.drafts : []);
              setVixAiConversations(Array.isArray(conversationResponse.conversations) ? conversationResponse.conversations : []);
            }
          }
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError.message || 'Failed to load Strategy Architect.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const applyDsl = (nextDsl, nextValidation) => {
    setDsl(nextDsl);
    setDslText(JSON.stringify(nextDsl, null, 2));
    setValidation(nextValidation || null);
    setCompileResult(null);
  };

  const handleGenerateDraft = async () => {
    if (!prompt.trim()) return;
    setAction('generate');
    setNotice('');
    setError('');
    try {
      const response = await generateStrategyDslDraft(prompt, token, {
        name: '',
      });
      setProvider(response.aiProvider || provider);
      applyDsl(response.dsl, response.validation);
      setCompileResult(response.compileResult || response.vixai?.compileResult || null);
      setNotice('Structured DSL draft created. It is experimental until execution/export support is certified.');
    } catch (generateError) {
      setError(generateError.message || 'Failed to generate DSL draft.');
    } finally {
      setAction('');
    }
  };

  const handleValidateEditor = async () => {
    setAction('validate');
    setNotice('');
    setError('');
    try {
      const parsed = JSON.parse(dslText);
      const response = await validateStrategyDsl(parsed);
      setDsl(parsed);
      setValidation(response.validation || null);
      setCompileResult(null);
      setNotice(response.validation?.valid ? 'DSL validates structurally.' : 'DSL has validation errors.');
    } catch (validateError) {
      setError(validateError.payload?.validation?.issues?.[0]?.message || validateError.message || 'Invalid JSON or DSL schema.');
      if (validateError.payload?.validation) {
        setValidation(validateError.payload.validation);
      }
    } finally {
      setAction('');
    }
  };

  const handleSaveDraft = async () => {
    if (!dsl) return;
    setAction('save');
    setNotice('');
    setError('');
    try {
      const response = await saveStrategyDslDraft({ dsl, prompt }, token);
      setNotice(`Saved "${response.draft?.title || dsl.meta?.name || 'DSL draft'}".`);
      await refreshDrafts();
    } catch (saveError) {
      setError(saveError.message || 'Failed to save DSL draft.');
      if (saveError.payload?.validation) {
        setValidation(saveError.payload.validation);
      }
    } finally {
      setAction('');
    }
  };

  const handleCompileDsl = async () => {
    if (!dslText.trim()) return;
    setAction('compile');
    setNotice('');
    setError('');
    try {
      const parsed = JSON.parse(dslText);
      const response = await compileStrategyDsl(parsed, token);
      setDsl(parsed);
      setValidation(response.compileResult?.validation || validation);
      setCompileResult(response.compileResult || null);
      setNotice(response.compileResult?.canCreateBuilderStrategy
        ? 'DSL is Builder-ready. You can create a saved Builder prototype.'
        : 'DSL analyzed. Custom features that are not Builder-certified are listed in the setup plan.');
    } catch (compileError) {
      setError(compileError.payload?.compileResult?.plan?.[0]?.message || compileError.message || 'Failed to compile DSL.');
      if (compileError.payload?.compileResult) {
        setCompileResult(compileError.payload.compileResult);
        setValidation(compileError.payload.compileResult.validation || validation);
      }
    } finally {
      setAction('');
    }
  };

  const handleCreateBuilderStrategy = async () => {
    if (!dsl) return;
    setAction('create-builder');
    setNotice('');
    setError('');
    try {
      const response = await createBuilderStrategyFromDsl(dsl, token);
      if (response.strategy?.id) {
        navigate(`/tester?strategy=${response.strategy.id}`);
        return;
      }
      setNotice('Builder strategy created.');
    } catch (createError) {
      setError(createError.message || 'Failed to create Builder strategy.');
      if (createError.payload?.compileResult) {
        setCompileResult(createError.payload.compileResult);
      }
    } finally {
      setAction('');
    }
  };

  const handleLoadDraft = async (draftId) => {
    setAction(`load:${draftId}`);
    setNotice('');
    setError('');
    try {
      const response = await fetchStrategyDslDraft(draftId, token);
      applyDsl(response.draft?.dsl, response.draft?.validation);
      setPrompt(response.draft?.prompt || prompt);
      setNotice(`Loaded "${response.draft?.title || 'DSL draft'}".`);
    } catch (loadError) {
      setError(loadError.message || 'Failed to load DSL draft.');
    } finally {
      setAction('');
    }
  };

  const handleNewVixAiChat = () => {
    setVixAiConversationId(null);
    setVixAiMessages(INITIAL_VIXAI_MESSAGES);
    setVixAiInput('');
    setNotice('Started a new VixAI conversation.');
  };

  const handleLoadVixAiConversation = async (conversationId) => {
    setAction(`load-vixai:${conversationId}`);
    setNotice('');
    setError('');
    try {
      const response = await fetchVixAiConversation(conversationId, token);
      const messages = Array.isArray(response.messages) ? response.messages : [];
      setVixAiConversationId(response.conversation?.id || conversationId);
      setVixAiMessages(messages.length ? messages.map((item) => ({
        role: item.role,
        content: item.content,
        readiness: item.compileResult?.readiness || '',
      })) : INITIAL_VIXAI_MESSAGES);

      const latestWithDsl = [...messages].reverse().find((item) => item.dsl);
      if (latestWithDsl?.dsl) {
        setDsl(latestWithDsl.dsl);
        setDslText(JSON.stringify(latestWithDsl.dsl, null, 2));
        setValidation(latestWithDsl.validation || null);
        setCompileResult(latestWithDsl.compileResult || null);
      }
      const latestUserMessage = [...messages].reverse().find((item) => item.role === 'user');
      if (latestUserMessage?.content) {
        setPrompt(latestUserMessage.content);
      }
      setProvider((current) => ({
        ...(current || {}),
        provider: response.conversation?.provider || current?.provider,
        model: response.conversation?.model || current?.model,
        configured: response.conversation?.provider === 'openai' || !!current?.configured,
        mode: response.conversation?.provider === 'openai' ? 'openai_structured_dsl' : (current?.mode || 'local_structured_vixai'),
      }));
      setNotice(`Loaded "${response.conversation?.title || 'VixAI conversation'}".`);
    } catch (loadError) {
      setError(loadError.message || 'Failed to load VixAI conversation.');
    } finally {
      setAction('');
    }
  };

  const handleDownload = () => {
    if (!dsl) return;
    downloadJson(`${safeFileName(dsl.meta?.name)}.vixdsl.json`, dsl);
  };

  const handleSendVixAi = async (overrideMessage = '') => {
    const message = (overrideMessage || vixAiInput).trim();
    if (!message) return;
    setAction('vixai');
    setNotice('');
    setError('');
    setVixAiMessages((current) => [...current, { role: 'user', content: message }]);
    try {
      const response = await sendVixAiMessage({
        message,
        dsl,
        conversationId: vixAiConversationId,
        context: {
          name: dsl?.meta?.name || '',
          symbol: dsl?.builderHints?.symbol || '',
          timeframe: dsl?.builderHints?.timeframe || '',
          modelingMode: dsl?.builderHints?.modelingMode || '',
        },
      }, token);
      const result = response.vixai || {};
      if (response.conversationId) {
        setVixAiConversationId(response.conversationId);
      }
      if (result.dsl) {
        setDsl(result.dsl);
        setDslText(JSON.stringify(result.dsl, null, 2));
      }
      setValidation(result.validation || null);
      setCompileResult(result.compileResult || null);
      setProvider({
        provider: result.provider || (result.modelConfigured ? 'openai' : 'local'),
        mode: result.mode || 'local_structured_vixai',
        configured: !!result.modelConfigured,
        model: result.model || '',
        note: result.notes?.[0] || 'VixAI is enforcing the DSL and Builder readiness gates.',
      });
      setPrompt(message);
      setVixAiInput('');
      const providerIssue = result.providerError ? `\n\nAI provider issue:\n- ${result.providerError}` : '';
      setVixAiMessages((current) => [...current, {
        role: 'assistant',
        content: `${result.assistantMessage || 'I updated the DSL draft.'}${providerIssue}`,
        actions: result.suggestedActions || [],
        readiness: result.compileResult?.readiness || '',
      }]);
      await refreshVixAiConversations();
    } catch (vixAiError) {
      setError(vixAiError.message || 'VixAI could not process the request.');
      setVixAiMessages((current) => [...current, {
        role: 'assistant',
        content: vixAiError.message || 'VixAI could not process the request.',
        actions: [],
      }]);
    } finally {
      setAction('');
    }
  };

  return (
    <HelmetProvider>
      <div className="strategy-architect-page page-shell">
        <Helmet>
          <title>VixAI Strategy Agent - VixAlgoBuilder</title>
          <meta
            name="description"
            content="Chat with VixAI to design custom non-indicator strategies, inputs, panels, and visuals using the VixAlgoBuilder Strategy DSL."
          />
        </Helmet>

        <section className="architect-hero">
          <div>
            <span className="tag">VixAI Strategy Agent</span>
            <h1 className="page-title">Chat your EA idea into a controlled Builder specification.</h1>
            <p className="page-lead">
              This is the foundation for custom non-indicator strategies, user-defined inputs, info panels,
              persistent visuals, and future AI-assisted EA generation without sacrificing MT5 parity discipline.
            </p>
            <div className="architect-actions">
              <Link to="/tester" className="primary-cta">Open Builder</Link>
              <Link to="/builder-docs" className="ghost-cta">Read DSL Guidance</Link>
              <Link to="/tester-premium" className="ghost-cta">VixPremium</Link>
            </div>
          </div>
          <div className="architect-status-card">
            <span>Current mode</span>
            <strong>{providerModeLabel(provider)}</strong>
            {provider?.model ? <small className="architect-status-meta">Model: {provider.model}</small> : null}
            <p>{provider?.note || 'AI model provider is not connected yet. DSL and certification gates come first.'}</p>
          </div>
        </section>

        {!isSignedIn && (
          <section className="section-card architect-gate">
            <h2>Sign in required.</h2>
            <p className="muted-text">
              VixAI is a VixPremium workflow because it stores custom strategy DSL drafts and will eventually use AI generation resources.
            </p>
            <div className="architect-actions">
              <Link to="/login" className="primary-cta">Sign In</Link>
              <Link to="/signup" className="ghost-cta">Create Account</Link>
            </div>
          </section>
        )}

        {isSignedIn && !loading && !hasVixPremium && (
          <section className="section-card architect-gate">
            <h2>VixPremium required.</h2>
            <p className="muted-text">
              VixAI strategy drafts, saved DSL specs, and future compile checks are reserved for VixPremium users.
            </p>
            <Link to="/tester-premium" className="primary-cta">Activate VixPremium</Link>
          </section>
        )}

        {isSignedIn && hasVixPremium && (
          <section className="architect-layout">
            <aside className="section-card architect-sidebar">
              <h3>DSL Capability Map</h3>
              <p className="muted-text">
                Version: <strong>{schema?.version || 'loading'}</strong>
              </p>
              <div className="architect-chip-grid">
                {schema?.ruleNodeTypes?.map((type) => (
                  <span key={type} className={schema.builderCompilableRuleNodeTypes?.includes(type) ? 'candidate' : ''}>
                    {type.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
              <h3>Saved Drafts</h3>
              <div className="architect-draft-list">
                {drafts.length ? drafts.map((draft) => (
                  <button
                    key={draft.id}
                    type="button"
                    className="architect-draft-card"
                    onClick={() => handleLoadDraft(draft.id)}
                    disabled={action === `load:${draft.id}`}
                  >
                    <strong>{draft.title}</strong>
                    <small>{draft.certification || draft.status}</small>
                  </button>
                )) : <p className="muted-text">No saved DSL drafts yet.</p>}
              </div>
              <h3>VixAI Chats</h3>
              <button
                type="button"
                className="ghost-button compact-button"
                onClick={handleNewVixAiChat}
              >
                New VixAI Chat
              </button>
              <div className="architect-draft-list">
                {vixAiConversations.length ? vixAiConversations.map((conversation) => (
                  <button
                    key={conversation.id}
                    type="button"
                    className={`architect-draft-card ${conversation.id === vixAiConversationId ? 'active' : ''}`}
                    onClick={() => handleLoadVixAiConversation(conversation.id)}
                    disabled={action === `load-vixai:${conversation.id}`}
                  >
                    <strong>{conversation.title}</strong>
                    <small>
                      {[conversation.provider || 'local', conversation.model, formatShortDate(conversation.lastMessageAt)]
                        .filter(Boolean)
                        .join(' | ')}
                    </small>
                  </button>
                )) : <p className="muted-text">No saved VixAI chats yet.</p>}
              </div>
            </aside>

            <main className="architect-main">
              {error ? <div className="section-card architect-error">{error}</div> : null}
              {notice ? <div className="section-card architect-notice">{notice}</div> : null}

              <section className="section-card vixai-panel">
                <div className="architect-section-heading">
                  <div>
                    <span className="tag">VixAI</span>
                    <h2>Strategy Agent</h2>
                    <p className="muted-text">
                      Chat with VixAI to create or refine custom strategy DSL drafts. It will show whether the result is Builder-ready or blocked by unsupported custom logic.
                    </p>
                  </div>
                  <div className="vixai-heading-actions">
                    <span className={`vixai-provider-pill ${provider?.configured ? 'online' : 'fallback'}`}>
                      {provider?.configured ? 'Model backed' : 'Local fallback'}
                    </span>
                    <button
                      type="button"
                      className="ghost-button compact-button"
                      onClick={handleNewVixAiChat}
                    >
                      New Chat
                    </button>
                    <button
                      type="button"
                      className="ghost-button compact-button"
                      onClick={() => handleSendVixAi(EXAMPLE_PROMPT)}
                      disabled={action === 'vixai'}
                    >
                      Try Example
                    </button>
                  </div>
                </div>
                <div className="vixai-messages">
                  {vixAiMessages.map((message, index) => (
                    <article key={`${message.role}-${index}`} className={`vixai-message ${message.role}`}>
                      <strong>{message.role === 'assistant' ? 'VixAI' : 'You'}</strong>
                      <p>{message.content}</p>
                      {message.readiness ? <span className="vixai-readiness">{message.readiness}</span> : null}
                      {message.actions?.length ? (
                        <div className="vixai-action-tags">
                          {message.actions.map((item) => <span key={item}>{item.replace(/_/g, ' ')}</span>)}
                        </div>
                      ) : null}
                    </article>
                  ))}
                </div>
                <form
                  className="vixai-input-row"
                  onSubmit={(event) => {
                    event.preventDefault();
                    handleSendVixAi();
                  }}
                >
                  <textarea
                    value={vixAiInput}
                    onChange={(event) => setVixAiInput(event.target.value)}
                    rows={3}
                    placeholder="Example: Create an Asian range breakout for London, max 2 trades, trailing stop after 600 points."
                  />
                  <button type="submit" className="primary-cta compact-button" disabled={action === 'vixai' || !vixAiInput.trim()}>
                    {action === 'vixai' ? 'Thinking...' : 'Send to VixAI'}
                  </button>
                </form>
              </section>

              <section className="section-card architect-prompt-card">
                <div className="architect-section-heading">
                  <div>
                    <h2>Strategy Prompt</h2>
                    <p className="muted-text">
                      Describe custom logic, inputs, visuals, panels, sessions, and management rules. The result is a DSL draft, not raw MQ5.
                    </p>
                  </div>
                  <button type="button" className="ghost-button compact-button" onClick={() => setPrompt(EXAMPLE_PROMPT)}>
                    Load Example
                  </button>
                </div>
                <textarea
                  value={prompt}
                  onChange={(event) => setPrompt(event.target.value)}
                  rows={8}
                  placeholder="Describe a custom strategy..."
                />
                <div className="architect-actions">
                  <button
                    type="button"
                    className="primary-cta compact-button"
                    onClick={handleGenerateDraft}
                    disabled={action === 'generate' || !prompt.trim()}
                  >
                    {action === 'generate' ? 'Generating...' : 'Generate DSL Draft'}
                  </button>
                  <button type="button" className="ghost-button compact-button" onClick={handleValidateEditor} disabled={!dslText || action === 'validate'}>
                    {action === 'validate' ? 'Validating...' : 'Validate JSON'}
                  </button>
                  <button type="button" className="ghost-button compact-button" onClick={handleSaveDraft} disabled={!dsl || action === 'save'}>
                    {action === 'save' ? 'Saving...' : 'Save Draft'}
                  </button>
                  <button type="button" className="ghost-button compact-button" onClick={handleCompileDsl} disabled={!dslText || action === 'compile'}>
                    {action === 'compile' ? 'Analyzing...' : 'Analyze Builder Readiness'}
                  </button>
                  <button
                    type="button"
                    className="ghost-button compact-button"
                    onClick={handleCreateBuilderStrategy}
                    disabled={!compileResult?.canCreateBuilderStrategy || action === 'create-builder'}
                  >
                    {action === 'create-builder' ? 'Creating...' : 'Create Builder Prototype'}
                  </button>
                  <button type="button" className="ghost-button compact-button" onClick={handleDownload} disabled={!dsl}>
                    Download DSL
                  </button>
                </div>
              </section>

              {compileResult ? (
                <section className={`section-card architect-compile ${compileResult.readiness}`}>
                  <div className="architect-section-heading">
                    <div>
                      <h2>Builder Readiness</h2>
                      <p className="muted-text">
                        Status: <strong>{compileResult.readiness}</strong>
                        {compileResult.canCreateBuilderStrategy ? ' · Safe Builder prototype available.' : ' · Custom work required before Builder execution/export.'}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="primary-cta compact-button"
                      onClick={handleCreateBuilderStrategy}
                      disabled={!compileResult.canCreateBuilderStrategy || action === 'create-builder'}
                    >
                      {action === 'create-builder' ? 'Creating...' : 'Create Builder Prototype'}
                    </button>
                  </div>
                  {compileResult.warnings?.length ? (
                    <div className="architect-warning-list">
                      {compileResult.warnings.map((warning) => <p key={warning}>{warning}</p>)}
                    </div>
                  ) : null}
                  <div className="architect-plan-list">
                    {compileResult.plan?.map((step, index) => (
                      <article key={`${step.area}-${index}`} className={step.status}>
                        <span>{step.status}</span>
                        <strong>{step.area}</strong>
                        <p>{step.message}</p>
                      </article>
                    ))}
                  </div>
                </section>
              ) : null}

              <section className="architect-results-grid">
                <article className={`section-card architect-validation ${validationClass}`}>
                  <h3>Validation</h3>
                  {validation ? (
                    <>
                      <strong>{validation.valid ? validation.certification : 'failed'}</strong>
                      <p className="muted-text">
                        Inputs: {dslStats.inputs} · Rule groups: {dslStats.ruleGroups} · Visuals: {dslStats.visuals} · Panels: {dslStats.panels}
                      </p>
                      {validation.issues?.length ? (
                        <div className="architect-issue-list">
                          {validation.issues.map((issue, index) => (
                            <p key={`${issue.path}-${index}`} className={issue.severity}>
                              <span>{issue.severity}</span> {issue.path}: {issue.message}
                            </p>
                          ))}
                        </div>
                      ) : <p className="muted-text">No structural errors.</p>}
                      {validation.certificationNotes?.length ? (
                        <div className="architect-cert-list">
                          {validation.certificationNotes.map((note) => <p key={note}>{note}</p>)}
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <p className="muted-text">Generate or paste a DSL draft to validate it.</p>
                  )}
                </article>

                <article className="section-card architect-policy">
                  <h3>Export Policy</h3>
                  <p className="muted-text">
                    AI-created strategies stay experimental until every custom rule, input, panel, and visual is implemented in:
                  </p>
                  <ul>
                    <li>Backend backtest engine</li>
                    <li>Frontend replay and chart renderer</li>
                    <li>Exported HTML report</li>
                    <li>Generated MQ5 EA source</li>
                    <li>Parity/compile validation gate</li>
                  </ul>
                </article>
              </section>

              <section className="section-card architect-editor">
                <h2>Strategy DSL JSON</h2>
                <textarea
                  value={dslText}
                  onChange={(event) => {
                    setDslText(event.target.value);
                    setCompileResult(null);
                  }}
                  rows={22}
                  spellCheck="false"
                  placeholder="Generated DSL JSON will appear here."
                />
              </section>
            </main>
          </section>
        )}
      </div>
    </HelmetProvider>
  );
}

export default StrategyArchitectPage;
